// #region Imports
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import SaveIcon from "@mui/icons-material/Save";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { skipToken } from "@reduxjs/toolkit/query";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleBillingCode from "_shared/fee-schedules/categories/billing-codes/fee-schedule-billing-code";
import FeeScheduleCategoryDescription from "_shared/fee-schedules/categories/descriptions/fee-schedule-category-description";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import Guid from "_shared/guids/guid";
import CurrencyInput from "_shared/money/currencies/_views/currency-input";
import { Currency } from "_shared/money/currencies/currency";
import Money from "_shared/money/money";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency, Masked, MaskedFeeSchedule } from "work/masked-fields";
import FeeScheduleCategory from "../fee-schedule-category";
// #endregion

// #region Styled Components
const FormContainer = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
}));
const FormFields = styled("div")(() => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
}));
const NameTextField = styled(TextField)(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const DescriptionTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const PerSymbol = styled('span')(({ theme }) => ({
  fontSize: "2em",
  lineHeight: "1em",
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));
const BillingCodeField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
  width: "100%",
}));
/**
 * Rate, per, unit — on one row, and able to shrink to the panel it is drawn in.
 *
 * `minmax(0, …)` rather than a bare `2fr auto 1fr`: an `fr` track's floor is `auto`, so the tracks
 * refused to go below their contents' own minimum widths and the row stayed the width it wanted
 * whatever the panel could spare. This editor is drawn in a side panel that is `clamp(320px, 22vw,
 * 400px)` wide, so on any window below about 1650px the row was wider than the column it is in and
 * the panel scrolled sideways — with the unit field's own control hanging off the right-hand edge.
 *
 * `side-panel-width` puts the panel's 320px floor at exactly "the width where the category editor's
 * two-column money fields still fit side by side". This is what makes that true.
 */
const FeeRate = styled('div')(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(0, 2fr) auto minmax(0, 1fr)",
  marginTop: theme.spacing(2),
}));
/**
 * The unit field asks for 8rem and settles for its share of the row.
 *
 * It used to hold 8rem as a hard floor and give it up only under `breakpoints.down("sm")` — a
 * question about the *window*, asked by a control whose width comes from a panel that is 320px wide
 * on a 1440px display. The window was never below `sm`, so the floor never lifted; this is #4964's
 * mistake in miniature. The preferred width stays, as a width the track hands over when it has it.
 */
const UnitAutocomplete = styled(Autocomplete)(() => ({
  minWidth: 0,
  width: "8rem",
  maxWidth: "100%",
  "& .MuiInputBase-root": {
    width: "100%",
  },
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  flexShrink: 0,
  marginTop: "auto",
  paddingBottom: theme.spacing(8),
  paddingTop: theme.spacing(3),
}));
const ButtonTooltipTarget = styled("span")(() => ({
  display: "grid",
}));
const UNIT_OPTIONS = ["Hour", "Day", "Week", "Month", "Year", "Each"] as const;
// #endregion

// #region Types
type EditableFeeScheduleCategory = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
type SelectedFeeScheduleCategories = EditableFeeScheduleCategory[] | MaskedFeeSchedule;
type TeamMemberOption = { member: EntityMember; label: string };

type EditFeeScheduleCategoryProps = {
  category?: EditableFeeScheduleCategory;
  onCancel?: () => void;
  onCategoryAdded?: (category: EditableFeeScheduleCategory) => void;
  onCategoryUpdated?: (
    originalName: FeeScheduleCategoryName | null,
    category: EditableFeeScheduleCategory,
  ) => void;
  selectedCategories?: SelectedFeeScheduleCategories;
  teamMembers?: readonly EntityMember[];
  /**
   * Whether the fee may be left for the other side to set. Defaults to true, which is what a work
   * agreement or a proposal wants: a rate the two parties have not agreed yet is a real state there.
   *
   * A marketplace team's published rates have no other side to defer to, and the API contract has
   * no way to say "no fee" — `FeeScheduleCategoryCommand.FeeRate` is a plain `decimal`, so an
   * omitted rate binds to `0` and persists as a $0 fixed fee. Callers in that position pass false.
  */
  allowDeferredFee?: boolean;
  /**
   * Whether naming somebody is a note rather than a billing assignment. Defaults to false.
   *
   * On a proposal, a work agreement or a team's published rates, the person named on an hourly rate
   * is the person who will bill it — so an hourly rate must name one, and only a rate that has a
   * price to bill can. `WorkAgreement.ValidateFeeSchedule` is where that finally holds.
   *
   * A fee schedule TEMPLATE is a rate card and not an engagement. It says who usually does this
   * work, which is worth recording whatever shape the fee takes: a template rate that leaves the
   * price to the other side still has somebody in mind for it. So callers in that position pass
   * true, and the field becomes optional and stays offered.
   */
  teamMemberIsOptional?: boolean;
  /**
   * Opens a picker for somebody who is not on `teamMembers`, resolving to whoever was chosen, or to
   * undefined when the picker was dismissed. Omitted where the list is the whole of the answer.
   *
   * A template's rate may name anyone its author would put on a team — the firm they always hire,
   * somebody in their network, a bookmarked profile — because the person who bills an hourly rate
   * belongs to the vendor, and the author is not always the vendor. A proposal passes nothing: its
   * vendor team is the only list, which is exactly what is enforced before a hire.
   */
  onBrowseForTeamMember?: () => Promise<EntityMember | undefined>;
  fieldErrors?: Readonly<Record<string, string>>;
};

export default function EditFeeScheduleCategory(props: Readonly<EditFeeScheduleCategoryProps>) {
  const { category, onCancel, onCategoryAdded, onCategoryUpdated, selectedCategories, teamMembers, fieldErrors } = props;
  const { onBrowseForTeamMember } = props;
  const teamMemberAssignmentEnabled = teamMembers !== undefined;
  const allowDeferredFee = props.allowDeferredFee ?? true;
  const teamMemberIsOptional = props.teamMemberIsOptional ?? false;

  // #region State
  const [formDirty, setFormDirty] = useState(false);
  const [formName, setFormName] = useState<string>(category?.name?.value ?? "");
  const [formDescription, setFormDescription] = useState<string>(category?.description?.value ?? "");
  const [formBillingCode, setFormBillingCode] = useState<string>(category?.billingCode?.value ?? "");

  // Where deferral is not on offer there is no such thing as a rate nobody set, so a category that
  // arrives without one opens with the field empty and the form invalid until it is filled in —
  // rather than with the zero that would otherwise save as a $0 fixed fee.
  const [formFeeRate, setFormFeeRate] = useState<Money | undefined>(
    category instanceof FeeScheduleCategoryMaskedCurrency
      ? undefined
      : (category?.fee?.rate ?? (allowDeferredFee ? new Money(0, Currency.USD) : undefined)),
  );
  const [formFeeUnit, setFormFeeUnit] = useState<string | undefined>(
    category instanceof FeeScheduleCategoryMaskedCurrency ? undefined : (category?.fee?.unit ?? ""),
  );
  const [isFixedFee, setIsFixedFee] = useState<boolean | undefined>(
    category instanceof FeeScheduleCategoryMaskedCurrency ? undefined : Boolean(category && !category.fee?.unit),
  );
  // A category that arrived deferred opens un-deferred where deferral is not on offer, so its rate
  // has to be filled in before it can be saved.
  const [isDeferred, setIsDeferred] = useState<boolean | undefined>(
    category instanceof FeeScheduleCategoryMaskedCurrency ? undefined : allowDeferredFee && !category?.fee,
  );
  const [formTeamMember, setFormTeamMember] = useState<EntityMember | null>(
    teamMemberAssignmentEnabled ? (category?.teamMember ?? null) : null,
  );
  // #endregion

  // The one rule this editor enforces about staffing: an hourly rate names who bills it. Read once,
  // because the field's `required`, its helper text and `formValid` must not be able to disagree.
  const teamMemberIsRequired =
    teamMemberAssignmentEnabled &&
    !teamMemberIsOptional &&
    !isDeferred &&
    !isFixedFee &&
    Fee.isHourlyUnit(formFeeUnit);

  // #region Hooks
  const session = useSession();
  const availableTeamMembers = useMemo(() => teamMembers ?? [], [teamMembers]);
  const teamMemberUserIds = useMemo(
    () => new Set(availableTeamMembers.map((member) => member.userId.value)),
    [availableTeamMembers],
  );
  const individualsQuery = useGetIndividualsByUserIdsQuery(
    teamMemberAssignmentEnabled && teamMemberUserIds.size > 0 ? { userIds: teamMemberUserIds } : skipToken,
  );
  const teamMemberOptions = useMemo<TeamMemberOption[]>(() => {
    const namesByUserId = new Map(
      (Individual.fromJSONArray(individualsQuery.data, { allowEmpty: true }) ?? []).map((individual) => [
        individual.userId.value,
        individual.fullName,
      ]),
    );
    const members = [...availableTeamMembers];
    if (formTeamMember && !members.some((member) => member.isEqualTo(formTeamMember))) members.push(formTeamMember);
    return members.map((member) => ({
      member,
      label: namesByUserId.get(member.userId.value) ?? "Team member",
    }));
  }, [availableTeamMembers, formTeamMember, individualsQuery.data]);

  // Masked staffing is somebody else's to change, whatever the surface. Otherwise the field follows
  // whether naming somebody is a billing assignment: a rate with no price to bill cannot assign one,
  // while a rate card may say who does the work however its fee is expressed.
  const teamMemberDisabled = Boolean(
    category?.teamMemberIsMasked || (!teamMemberIsOptional && (isDeferred || isFixedFee)),
  );

  async function handleBrowseForTeamMember() {
    const chosen = await onBrowseForTeamMember?.();
    if (!chosen) return;
    setFormTeamMember(chosen);
    setFormDirty(true);
  }

  // Sits inside the field rather than beside it, so the list and the wider search read as one
  // control. The list is the fast answer for a firm naming its own people; this is the rest of the
  // answer, and the only one a client has.
  const browseForTeamMemberAction = onBrowseForTeamMember && !teamMemberDisabled && (
    <Tooltip title="Find someone else">
      <IconButton
        aria-label="Find a team member"
        edge="end"
        onClick={() => void handleBrowseForTeamMember()}
        size="small"
      >
        <PersonSearchOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  // #region Functions
  function formValid(): boolean {
    if (category instanceof FeeScheduleCategoryMaskedCurrency) {
      return Boolean(formName.trim() && formDescription.trim());
    }
    return Boolean(
      formName.trim() &&
      formDescription.trim() &&
      (isDeferred ||
        (formFeeRate &&
          (isFixedFee ||
            (formFeeUnit &&
              formFeeUnit.trim().length > 0 &&
              (!teamMemberIsRequired || formTeamMember))))),
    );
  }

  function handleAddCategoryClicked(): void {
    const newCategory = getStateAsFeeScheduleCategory();
    if (!newCategory) return;
    onCategoryAdded?.(newCategory);
  }

  function handleSaveCategoryClicked(originalName: FeeScheduleCategoryName | null): void {
    if (!category) return;

    const updatedCategory = getStateAsFeeScheduleCategory();
    if (!updatedCategory) return;
    onCategoryUpdated?.(originalName, updatedCategory);
  }

  function getStateAsFeeScheduleCategory(): FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | undefined {
    if (!formName.trim() || !formDescription.trim()) return;
    const normalizedName = formName.trim();
    const normalizedDescription = formDescription.trim();
    const normalizedUnit = formFeeUnit?.trim() ?? "";
    if (category instanceof FeeScheduleCategoryMaskedCurrency) {
      return new FeeScheduleCategoryMaskedCurrency({
        id: category.id,
        name: new FeeScheduleCategoryName(normalizedName),
        description: new FeeScheduleCategoryDescription(normalizedDescription),
        billingCode: formBillingCode ? new FeeScheduleBillingCode(formBillingCode) : null,
        teamMember: teamMemberAssignmentEnabled ? formTeamMember : null,
        teamMemberIsMasked: teamMemberAssignmentEnabled && category.teamMemberIsMasked,
        version: category.version,
      });
    }
    if (formFeeRate === undefined || formFeeUnit === undefined || isFixedFee === undefined || isDeferred === undefined)
      return;
    let fee: Fee | null;
    if (isDeferred) {
      fee = null;
    } else if (isFixedFee) {
      fee = Fee.create({ rate: formFeeRate, unit: null });
    } else {
      if (!normalizedUnit) return;
      fee = Fee.create({ rate: formFeeRate, unit: normalizedUnit });
    }

    return new FeeScheduleCategory({
      name: new FeeScheduleCategoryName(normalizedName),
      description: new FeeScheduleCategoryDescription(normalizedDescription),
      fee,
      billingCode: formBillingCode ? new FeeScheduleBillingCode(formBillingCode) : null,
      teamMember: teamMemberAssignmentEnabled ? formTeamMember : null,
      teamMemberIsMasked: teamMemberAssignmentEnabled && category?.teamMemberIsMasked,
      id: category?.id ?? Guid.generate(),
      version: category?.version,
    });
  }

  function getSaveDisabledTooltip(nameAlreadyExists: boolean): string {
    // Context-neutral, because this editor is now also reached from a marketplace team's profile
    // and from a work agreement, where there is no proposal to name.
    if (nameAlreadyExists) return "This category already exists in this fee schedule.";
    if (feeRateIsMissing()) return "Set a rate for this category.";
    return "";
  }

  function feeRateIsMissing(): boolean {
    if (category instanceof FeeScheduleCategoryMaskedCurrency) return false;
    return !isDeferred && formFeeRate === undefined;
  }

  function getDeferredLabel(): string {
    const accountType = session.context?.viewingAsVendor ? "client" : "vendor";
    if (!isDeferred) return `Let ${accountType} set fee?`;
    return `${accountType[0].toUpperCase() + accountType.slice(1)} to set fee`;
  }

  function categoryHasChanges(): boolean {
    if (!category) return formDirty;

    const updatedCategory = getStateAsFeeScheduleCategory();
    if (!updatedCategory) return false;

    if (
      category instanceof FeeScheduleCategoryMaskedCurrency &&
      updatedCategory instanceof FeeScheduleCategoryMaskedCurrency
    ) {
      return !feeScheduleCategoryBaseFieldsAreEqual(category, updatedCategory);
    }

    if (category instanceof FeeScheduleCategory && updatedCategory instanceof FeeScheduleCategory) {
      return !(
        feeScheduleCategoryBaseFieldsAreEqual(category, updatedCategory) &&
        ((!category.fee && !updatedCategory.fee) ||
          Boolean(
            category.fee &&
            updatedCategory.fee &&
            category.fee.rate.isEqualTo(updatedCategory.fee.rate) &&
            (category.fee.unit || null) === (updatedCategory.fee.unit || null),
          )) &&
        (!teamMemberAssignmentEnabled ||
          (((!category.teamMember && !updatedCategory.teamMember) ||
            category.teamMember?.isEqualTo(updatedCategory.teamMember)) &&
            category.teamMemberIsMasked === updatedCategory.teamMemberIsMasked))
      );
    }

    return true;
  }

  function categoryNameAlreadyExistsInThisFeeSchedule(): boolean {
    if (!formName.trim() || Masked.isMasked(selectedCategories)) return false;

    const formCategoryName = new FeeScheduleCategoryName(formName.trim());
    return selectedCategories?.some((selectedCategory) => {
      if (category?.id.isEqualTo(selectedCategory.id)) return false;
      return selectedCategory.name.isEqualTo(formCategoryName);
    }) ?? false;
  }

  function feeScheduleCategoryBaseFieldsAreEqual(
    originalCategory: EditableFeeScheduleCategory,
    updatedCategory: EditableFeeScheduleCategory,
  ): boolean {
    return Boolean(
      originalCategory.name.isEqualTo(updatedCategory.name) &&
      originalCategory.description?.isEqualTo(updatedCategory.description ?? null) &&
      ((!originalCategory.billingCode && !updatedCategory.billingCode) ||
        originalCategory.billingCode?.isEqualTo(updatedCategory.billingCode)),
    );
  }

  // #region Render
  const feeControlsDisabled = Boolean(isDeferred) || category instanceof FeeScheduleCategoryMaskedCurrency;
  const feeUnitDisabled = feeControlsDisabled || Boolean(isFixedFee);
  const categoryNameAlreadyExists = categoryNameAlreadyExistsInThisFeeSchedule();
  const saveDisabled = categoryNameAlreadyExists || !categoryHasChanges() || !formValid();
  const saveDisabledTooltip = getSaveDisabledTooltip(categoryNameAlreadyExists);

  function getFieldError(field: string): string | undefined {
    if (!fieldErrors) return undefined;
    const normalizedField = field.replace(/[._\-\s]/g, "").toLowerCase();
    const entry = Object.entries(fieldErrors).find(
      ([key]) => key.replace(/[._\-\s]/g, "").toLowerCase() === normalizedField,
    );
    return entry?.[1];
  }

  return (
    <FormContainer>
      <FormFields>
        <NameTextField
          label="Category"
          sx={{ marginTop: 1 }}
          value={formName}
          required
          error={Boolean(getFieldError("category"))}
          helperText={getFieldError("category")}
          onChange={(event) => {
            setFormName(event.target.value);
            setFormDirty(true);
          }}
        />
        <DescriptionTextField
          variant="outlined"
          label="Description Of Work"
          value={formDescription}
          multiline
          minRows={5}
          required
          error={Boolean(getFieldError("description"))}
          helperText={getFieldError("description")}
          onChange={(event) => {
            setFormDescription(event.target.value);
            setFormDirty(true);
          }}
        />
        {allowDeferredFee && (
          <FormControlLabel
            label={getDeferredLabel()}
            control={
              <Checkbox
                color="primary"
                checked={Boolean(isDeferred)}
                disabled={category instanceof FeeScheduleCategoryMaskedCurrency}
                onChange={() => {
                  setIsDeferred((prevValue) => !prevValue);
                  setFormDirty(true);
                }}
              />
            }
          />
        )}
        <BillingCodeField
          label="Billing Code (optional)"
          size="small"
          variant="outlined"
          value={formBillingCode ?? ""}
          onChange={(event) => {
            setFormBillingCode(event.target.value);
            setFormDirty(true);
          }}
        />
        <div>
          <FormControlLabel
            label="Fixed fee"
            labelPlacement="end"
            control={
              <Checkbox
                color="primary"
                checked={Boolean(isFixedFee)}
                disabled={feeControlsDisabled}
                onChange={() => {
                  setIsFixedFee((prevValue) => !prevValue);
                  setFormDirty(true);
                }}
              />
            }
          />
          <FeeRate>
            <CurrencyInput
              allowNegative={false}
              value={formFeeRate}
              size="small"
              disabled={feeControlsDisabled}
              onChange={(currency: Currency, amount: number | undefined) => {
                // A cleared field clears the rate, so `feeRateIsMissing` sees it and Save stays
                // disabled. Coercing to zero here would show an empty field beside an enabled
                // Save and publish a 0 rate - the state this editor's validation exists to stop.
                setFormFeeRate(amount === undefined ? undefined : new Money(amount, currency ?? "USD"));
                setFormDirty(true);
              }}
            />
            {(getFieldError("fee") || getFieldError("currency")) && (
              <FormHelperText error>{getFieldError("fee") ?? getFieldError("currency")}</FormHelperText>
            )}
            <PerSymbol>/</PerSymbol>
            <UnitAutocomplete
              freeSolo
              disabled={feeUnitDisabled}
              options={UNIT_OPTIONS}
              inputValue={formFeeUnit ?? ""}
              onInputChange={(_event, value) => {
                setFormFeeUnit(value);
                setFormDirty(true);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Unit"
                  size="small"
                  disabled={feeUnitDisabled}
                />
              )}
            />
          </FeeRate>
          {teamMemberAssignmentEnabled && (
            <Autocomplete<TeamMemberOption>
              disabled={teamMemberDisabled}
              options={teamMemberOptions}
              value={teamMemberOptions.find((option) => option.member.isEqualTo(formTeamMember)) ?? null}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.member.isEqualTo(value.member)}
              onChange={(_event, option) => {
                setFormTeamMember(option?.member ?? null);
                setFormDirty(true);
              }}
              sx={{ marginTop: 2 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={category?.teamMemberIsMasked ? "Team Member (restricted)" : "Team Member"}
                  error={Boolean(getFieldError("team"))}
                  helperText={
                    getFieldError("team")
                      ?? (teamMemberIsRequired && teamMemberOptions.length === 0 && !onBrowseForTeamMember
                        ? "Add a member to the vendor team before setting an hourly rate."
                        : undefined)
                  }
                  required={teamMemberIsRequired}
                  size="small"
                  slotProps={{
                    // Spread the whole of `params.slotProps`, not just `input`: `inputLabel` and
                    // `htmlInput` carry the label association and every keyboard handler the
                    // Autocomplete needs, and naming `input` alone would replace them with nothing.
                    ...params.slotProps,
                    input: {
                      ...params.slotProps.input,
                      endAdornment: (
                        <>
                          {browseForTeamMemberAction}
                          {params.slotProps.input.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          )}
        </div>
      </FormFields>
      <ButtonContainer>
        <Tooltip title={saveDisabledTooltip}>
          <ButtonTooltipTarget>
            <Button
              color="primary"
              variant="contained"
              disabled={saveDisabled}
              startIcon={<SaveIcon />}
              onClick={category
                ? () => handleSaveCategoryClicked(category.name)
                : handleAddCategoryClicked}
            >
              {category ? "Save" : "Add"}
            </Button>
          </ButtonTooltipTarget>
        </Tooltip>
        {onCancel && (
          <Button
            color="primary"
            variant="outlined"
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
      </ButtonContainer>
    </FormContainer>
  );
}

